import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

interface AnimatedCounterProps {
  end: number;
  suffix?: string;
  prefix?: string;
  label: string;
  duration?: number;
  decimals?: number;
}

const AnimatedCounter = ({ end, suffix = "", prefix = "", label, duration = 2, decimals = 0 }: AnimatedCounterProps) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isInView || hasAnimated.current) return;
    hasAnimated.current = true;

    const startTime = performance.now();
    const animate = (currentTime: number) => {
      const elapsed = (currentTime - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(eased * end);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  const displayValue = decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toString();

  return (
    <motion.div
      ref={ref}
      className="text-center group"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        className="notranslate font-display text-4xl md:text-6xl font-bold gold-gradient-text mb-2 text-glow"
        translate="no"
        whileHover={{ scale: 1.08 }}
        transition={{ duration: 0.3 }}
      >
        {prefix}{displayValue}{suffix}
      </motion.div>
      <div className="w-8 h-0.5 gold-gradient-bg mx-auto mb-2 rounded-full opacity-50" />
      <p className="text-muted-foreground text-xs md:text-sm tracking-[0.2em] uppercase">{label}</p>
    </motion.div>
  );
};

export default AnimatedCounter;
